/**
 * Restores user status from IndexedDB on load and persists on status changes.
 * Shows loading until restore completes.
 */

import { Spinner, SpinnerSize, Stack } from '@fluentui/react';
import IvicosStrings from 'kits/language/stringKit';
import { berlinDateString } from 'kits/berlinDateKit';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { RecoilValue } from 'recoil';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { idpService } from 'services/api/identity-provider.service';
import {
    aAtWorkLocation,
    aAtWorkCustomPhrase,
    aAwayStatusMessage,
    aFocusDurationMinutes,
    aFocusEndTime,
    aFocusTimeKnockDoor,
    aFocusTimeTextMessage,
    aOutOfOfficeAbsenceComment,
    aOutOfOfficePreferredMethods,
    aOutOfOfficeReturnDate,
    aOutOfOfficeReturnTime,
    aShowFocusExtendEndModal,
    aShowOnCallEndedModal,
    aShowAwayExtensionModal,
    aUserStatus,
    aOtherUsersStatusMap
} from './atoms';
import { aIdentityClaims } from './atoms';
import { sCanUseStatus } from './selectors';
import {
    getStoredStatus,
    getAllStoredStatuses,
    isSameStoredStatus,
    putStoredStatus,
    STATUS_CHANGED_STORAGE_KEY,
    type IStoredStatus,
    type IStoredStatusEmergencyContactBusy,
    type IStoredStatusEmergencyContactOoo
} from './statusPersistence';
import type { AtWorkLocation, UserStatus } from './types';

const DEBOUNCE_MS = 300;

/**
 * Builds backend user_status payload for API call.
 * Includes all fields including durationMinutes for countdown display.
 */
function buildBackendPayload(get: (atom: RecoilValue<any>) => any) {
    const status = get(aUserStatus) as UserStatus | undefined;
    if (!status) return null;

    const location = get(aAtWorkLocation) as AtWorkLocation;
    const atWorkCustomPhrase = get(aAtWorkCustomPhrase) as string | undefined;
    const awayStatusMessage = get(aAwayStatusMessage) as string | undefined;
    const focusEndTime = get(aFocusEndTime) as number | undefined;
    const focusDurationMinutes = get(aFocusDurationMinutes) as number | undefined;
    const knockDoor = get(aFocusTimeKnockDoor) as boolean;
    const textMessage = get(aFocusTimeTextMessage) as boolean;
    const returnDate = get(aOutOfOfficeReturnDate) as Date | undefined;
    const returnTime = get(aOutOfOfficeReturnTime) as string | undefined;
    const absenceComment = get(aOutOfOfficeAbsenceComment) as string;
    const preferredMethods = get(aOutOfOfficePreferredMethods);

    let phrase: string | null = null;
    let duration: number | null = null;
    let returnDateStr: string | null = null;
    let returnTimeStr: string | null = null;
    let absenceCommentStr: string | null = null;
    let emergencyContact: any = null;

    if (status === 'available') {
        phrase = location === 'custom' && atWorkCustomPhrase?.trim() ? atWorkCustomPhrase.trim() : IvicosStrings.statusAtWork;
    } else if (status === 'away') {
        phrase = awayStatusMessage ?? null;
    } else if (status === 'busy') {
        phrase = IvicosStrings.focusWorkUntilEnd;
        if (focusDurationMinutes != null && focusEndTime != null) {
            duration = focusDurationMinutes * 60 * 1000;
        }
        emergencyContact = { knockDoor, textMessage };
    } else if (status === 'out-of-office') {
        phrase = absenceComment?.trim() || null;
        absenceCommentStr = absenceComment?.trim() || null;
        if (returnDate) {
            returnDateStr = berlinDateString(returnDate);
        }
        if (returnTime) {
            returnTimeStr = returnTime;
        }
        emergencyContact = preferredMethods;
    } else if (status === 'on-the-phone') {
        phrase = IvicosStrings.onPhoneUntilEnd;
        if (focusDurationMinutes != null && focusEndTime != null) {
            duration = focusDurationMinutes * 60 * 1000;
        }
    }

    const endTime = status === 'busy' || status === 'on-the-phone' ? focusEndTime ?? null : null;
    const durationMinutes = status === 'busy' || status === 'on-the-phone' ? focusDurationMinutes ?? null : null;

    return {
        status,
        location: status === 'available' ? location : null,
        phrase,
        duration,
        endTime,
        durationMinutes,
        returnDate: returnDateStr,
        returnTime: returnTimeStr,
        absenceComment: absenceCommentStr,
        emergencyContact
    };
}

function buildPayloadFromSnapshot(get: (atom: RecoilValue<any>) => any, userId: string): IStoredStatus | null {
    const status = get(aUserStatus) as UserStatus | undefined;
    if (!status) return null;

    const location = get(aAtWorkLocation) as AtWorkLocation;
    const atWorkCustomPhrase = get(aAtWorkCustomPhrase) as string | undefined;
    const awayStatusMessage = get(aAwayStatusMessage) as string | undefined;
    const focusEndTime = get(aFocusEndTime) as number | undefined;
    const focusDurationMinutes = get(aFocusDurationMinutes) as number | undefined;
    const inviteToRoom = get(aFocusTimeKnockDoor) as boolean;
    const textMessage = get(aFocusTimeTextMessage) as boolean;
    const returnDate = get(aOutOfOfficeReturnDate) as Date | undefined;
    const returnTime = get(aOutOfOfficeReturnTime) as string | undefined;
    const absenceComment = get(aOutOfOfficeAbsenceComment) as string;
    const preferredMethods = get(aOutOfOfficePreferredMethods);

    let phrase: string | null = null;
    let duration: number | null = null;
    let returnDateStr: string | null = null;
    let returnTimeStr: string | null = null;
    let absenceCommentStr: string | null = null;
    let emergencyContact: IStoredStatus['emergencyContact'] = null;

    if (status === 'available') {
        phrase = location === 'custom' && atWorkCustomPhrase?.trim() ? atWorkCustomPhrase.trim() : IvicosStrings.statusAtWork;
    } else if (status === 'away') {
        phrase = awayStatusMessage ?? null;
    } else if (status === 'busy') {
        phrase = IvicosStrings.focusWorkUntilEnd;
        if (focusDurationMinutes != null && focusEndTime != null) {
            duration = focusDurationMinutes * 60 * 1000;
        }
        emergencyContact = { inviteToRoom: inviteToRoom, textMessage: textMessage } as IStoredStatusEmergencyContactBusy;
    } else if (status === 'out-of-office') {
        phrase = absenceComment?.trim() || null;
        absenceCommentStr = absenceComment?.trim() || null;
        if (returnDate) {
            returnDateStr = berlinDateString(returnDate);
        }
        if (returnTime) {
            returnTimeStr = returnTime;
        }
        emergencyContact = preferredMethods as IStoredStatusEmergencyContactOoo;
    } else if (status === 'on-the-phone') {
        phrase = IvicosStrings.onPhoneUntilEnd;
        if (focusDurationMinutes != null && focusEndTime != null) {
            duration = focusDurationMinutes * 60 * 1000;
        }
    }

    const endTime = status === 'busy' || status === 'on-the-phone' ? focusEndTime ?? null : null;

    return {
        userId,
        status,
        location: status === 'available' ? location : null,
        phrase,
        duration,
        endTime,
        returnDate: returnDateStr,
        returnTime: returnTimeStr,
        absenceComment: absenceCommentStr,
        emergencyContact,
        version: 1
    };
}

export interface IStatusPersistenceProviderProps {
    children: React.ReactNode;
}

export const StatusPersistenceProvider: React.FC<IStatusPersistenceProviderProps> = ({ children }) => {
    const [isRestored, setIsRestored] = useState(false);
    const identityClaims = useRecoilValue(aIdentityClaims);
    const userId = identityClaims?.uid;
    // visitors have no own status: nothing is restored, stored or sent to the backend for them
    const canUseStatus = useRecoilValue(sCanUseStatus);
    const location = useLocation();
    const persistTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const backendPersistTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const userStatus = useRecoilValue(aUserStatus);
    const atWorkLocation = useRecoilValue(aAtWorkLocation);
    const atWorkCustomPhrase = useRecoilValue(aAtWorkCustomPhrase);
    const awayStatusMessage = useRecoilValue(aAwayStatusMessage);
    const focusEndTime = useRecoilValue(aFocusEndTime);
    const focusDurationMinutes = useRecoilValue(aFocusDurationMinutes);
    const focusTimeKnockDoor = useRecoilValue(aFocusTimeKnockDoor);
    const focusTimeTextMessage = useRecoilValue(aFocusTimeTextMessage);
    const outOfOfficeReturnDate = useRecoilValue(aOutOfOfficeReturnDate);
    const outOfOfficeReturnTime = useRecoilValue(aOutOfOfficeReturnTime);
    const outOfOfficeAbsenceComment = useRecoilValue(aOutOfOfficeAbsenceComment);
    const outOfOfficePreferredMethods = useRecoilValue(aOutOfOfficePreferredMethods);

    const restore = useRecoilCallback(
        ({ set }) =>
            async (uid: string) => {
                try {
                    set(aShowAwayExtensionModal, false);
                    const stored = await getStoredStatus(uid);
                    if (!stored) {
                        setIsRestored(true);
                        return;
                    }

                    set(aUserStatus, stored.status as UserStatus);

                    if (stored.status === 'available' && stored.location) {
                        set(aAtWorkLocation, stored.location as AtWorkLocation);
                        if (stored.location === 'custom' && stored.phrase) {
                            set(aAtWorkCustomPhrase, stored.phrase);
                        }
                    }
                    if (stored.status === 'away' && stored.phrase) {
                        set(aAwayStatusMessage, stored.phrase);
                    }

                    if (stored.status === 'busy') {
                        const ec = stored.emergencyContact as IStoredStatusEmergencyContactBusy | null;
                        if (ec) {
                            set(aFocusTimeKnockDoor, ec.inviteToRoom);
                            set(aFocusTimeTextMessage, ec.textMessage);
                        }
                        const endTime = stored.endTime ?? (stored.duration != null ? Date.now() + stored.duration : null);
                        if (endTime != null && endTime <= Date.now()) {
                            // Expired: user reloaded without extending → restore as available
                            set(aUserStatus, 'available');
                            set(aFocusEndTime, undefined);
                            set(aFocusDurationMinutes, undefined);
                            set(aShowFocusExtendEndModal, false);
                        } else if (endTime != null && stored.duration != null) {
                            set(aFocusEndTime, endTime);
                            set(aFocusDurationMinutes, stored.duration / (60 * 1000));
                        }
                    }

                    if (stored.status === 'out-of-office') {
                        if (stored.returnDate) {
                            set(aOutOfOfficeReturnDate, new Date(stored.returnDate));
                        }
                        if (stored.returnTime) {
                            set(aOutOfOfficeReturnTime, stored.returnTime);
                        }
                        if (stored.absenceComment) {
                            set(aOutOfOfficeAbsenceComment, stored.absenceComment);
                        }
                        if (stored.emergencyContact) {
                            set(aOutOfOfficePreferredMethods, stored.emergencyContact as any);
                        }
                    }

                    if (stored.status === 'on-the-phone') {
                        const endTime = stored.endTime ?? (stored.duration != null ? Date.now() + stored.duration : null);
                        if (endTime != null && endTime <= Date.now()) {
                            // Expired: user reloaded without extending → restore as available
                            set(aUserStatus, 'available');
                            set(aFocusEndTime, undefined);
                            set(aFocusDurationMinutes, undefined);
                            set(aShowOnCallEndedModal, false);
                        } else if (endTime != null && stored.duration != null) {
                            set(aFocusEndTime, endTime);
                            set(aFocusDurationMinutes, stored.duration / (60 * 1000));
                        }
                    }
                } catch (err) {
                    console.warn('[StatusPersistence] Restore failed:', err);
                } finally {
                    setIsRestored(true);
                }
            },
        []
    );

    const persist = useRecoilCallback(
        ({ snapshot }) =>
            async (uid: string) => {
                const payload =
                    snapshot.getLoadable(aUserStatus).state === 'hasValue'
                        ? buildPayloadFromSnapshot((atom) => snapshot.getLoadable(atom as any).contents, uid)
                        : null;
                if (payload) {
                    try {
                        // Unchanged record: skip the write and the broadcast. This also ends the chain when a tab
                        // persists right after restoring another tab's change.
                        const stored = await getStoredStatus(uid);
                        if (isSameStoredStatus(stored, payload)) return;
                        await putStoredStatus(payload);
                        try {
                            localStorage.setItem(STATUS_CHANGED_STORAGE_KEY, String(Date.now()));
                        } catch {
                            // storage unavailable: other tabs sync on their next reload
                        }
                    } catch (err) {
                        console.warn('[StatusPersistence] Persist failed:', err);
                    }
                }
            },
        []
    );

    const persistToBackend = useRecoilCallback(
        ({ snapshot }) =>
            async () => {
                const backendPayload =
                    snapshot.getLoadable(aUserStatus).state === 'hasValue' ? buildBackendPayload((atom) => snapshot.getLoadable(atom as any).contents) : null;
                if (backendPayload) {
                    try {
                        const apiPayload = {
                            status: backendPayload.status,
                            location: backendPayload.location,
                            phrase: backendPayload.phrase ?? undefined,
                            duration: backendPayload.duration,
                            endTime: backendPayload.endTime,
                            durationMinutes: backendPayload.durationMinutes,
                            returnDate: backendPayload.returnDate,
                            returnTime: backendPayload.returnTime,
                            absenceComment: backendPayload.absenceComment,
                            emergencyContact: backendPayload.emergencyContact
                        };
                        await idpService.updateUser(undefined, undefined, undefined, undefined, undefined, undefined, undefined, apiPayload);
                    } catch (err) {
                        console.warn('[StatusPersistence] Backend persist failed:', err);
                    }
                }
            },
        []
    );

    const restoreOtherUsers = useRecoilCallback(
        ({ set }) =>
            async (currentUserId: string) => {
                try {
                    const allStatuses = await getAllStoredStatuses();
                    // Filter out current user and build map
                    const otherUsersMap: Record<string, IStoredStatus> = {};
                    for (const status of allStatuses) {
                        if (status.userId !== currentUserId) {
                            otherUsersMap[status.userId] = status;
                        }
                    }
                    if (Object.keys(otherUsersMap).length > 0) {
                        set(aOtherUsersStatusMap, otherUsersMap);
                    }
                } catch (err) {
                    console.warn('[StatusPersistence] Restore other users failed:', err);
                }
            },
        []
    );

    useEffect(() => {
        if (userId && canUseStatus) {
            setIsRestored(false);
            restore(userId).then(() => {
                restoreOtherUsers(userId);
            });
        } else {
            setIsRestored(true);
            if (userId) restoreOtherUsers(userId);
        }
    }, [userId, canUseStatus, location.pathname, restore, restoreOtherUsers]);

    useEffect(() => {
        if (!userId || !canUseStatus) return;
        // Another tab stored a changed status: adopt it without the restoring spinner.
        const onStorage = (event: StorageEvent) => {
            if (event.key === STATUS_CHANGED_STORAGE_KEY) void restore(userId);
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [userId, canUseStatus, restore]);

    const schedulePersist = useCallback(() => {
        if (!userId) return;
        if (persistTimeoutRef.current) clearTimeout(persistTimeoutRef.current);
        persistTimeoutRef.current = setTimeout(() => {
            persistTimeoutRef.current = null;
            persist(userId);
        }, DEBOUNCE_MS);
    }, [userId, persist]);

    const scheduleBackendPersist = useCallback(() => {
        if (!userId) return;
        if (backendPersistTimeoutRef.current) clearTimeout(backendPersistTimeoutRef.current);
        backendPersistTimeoutRef.current = setTimeout(() => {
            backendPersistTimeoutRef.current = null;
            persistToBackend();
        }, DEBOUNCE_MS);
    }, [userId, persistToBackend]);

    useEffect(() => {
        if (!userId || !canUseStatus || !isRestored) return;
        schedulePersist();
        scheduleBackendPersist();
        return () => {
            if (persistTimeoutRef.current) clearTimeout(persistTimeoutRef.current);
            if (backendPersistTimeoutRef.current) clearTimeout(backendPersistTimeoutRef.current);
        };
    }, [
        userId,
        canUseStatus,
        isRestored,
        schedulePersist,
        scheduleBackendPersist,
        userStatus,
        atWorkLocation,
        atWorkCustomPhrase,
        awayStatusMessage,
        focusEndTime,
        focusDurationMinutes,
        focusTimeKnockDoor,
        focusTimeTextMessage,
        outOfOfficeReturnDate,
        outOfOfficeReturnTime,
        outOfOfficeAbsenceComment,
        outOfOfficePreferredMethods
    ]);

    if (!isRestored && userId) {
        return (
            <Stack horizontalAlign="center" verticalAlign="center" style={{ height: '100%', minHeight: 120 }}>
                <Spinner size={SpinnerSize.medium} label={IvicosStrings.statusRestoring} />
            </Stack>
        );
    }

    return <>{children}</>;
};
